import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ChatMessage } from '../shared/ChatMessage';
import { ChatInput } from '../shared/ChatInput';
import { KPICard } from '../shared/KPICard';
import { SprintBurndown } from '../shared/SprintBurndown';
import { IssueList } from '../shared/IssueList';
import { AgentAvatar } from '../shared/AgentAvatar';
import { ActivityFeed } from '../shared/ActivityFeed';
import { SprintContextBar } from '../shared/SprintContextBar';
import { KanbanBoard } from '../workspace/KanbanBoard';
import { ChatHistorySidebar } from '../workspace/ChatHistorySidebar';
import { GlobalDashboard } from '../shared/GlobalDashboard';
import { ProjectSwitcher } from '../shared/ProjectSwitcher';
import { NotificationCenter, ToastContainer } from '../shared/NotificationCenter';
import { NotificationsProvider, useNotifications } from '../../hooks/useNotifications';
import { ProjectProvider, useProjectContext } from '../../hooks/useProjectContext';
import { LayoutGrid, MessageSquare, Calendar, List, Settings, Plus, Filter, ArrowUpDown, Sparkles, Bot, Kanban, History, Zap, Radio, Terminal, BookOpen, LogOut, ChevronRight, Home, CreditCard, Rocket, Github, HardDrive, Mail, ChevronDown, Plug, Hash, Activity, Table2, FileText, Presentation, ClipboardList, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { useChat } from '../../hooks/useChat';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useJiraData } from '../../hooks/useJiraData';
import { toMockIssue, toMockSprint, buildKPIs, buildMockProject } from '../../services/jiraData';
import { FloatingChat } from '../workspace/FloatingChat';
import { SettingsPanel } from '../workspace/SettingsPanel';
import { SetupWizard } from '../workspace/SetupWizard';
import { AgentMarketplace } from '../workspace/AgentMarketplace';
import { BillingPanel } from '../workspace/BillingPanel';
import { OnboardingPanel } from '../workspace/OnboardingPanel';
import { GitHubPanel } from '../workspace/GitHubPanel';
import { GoogleCalendarPanel } from '../workspace/GoogleCalendarPanel';
import { GoogleDrivePanel } from '../workspace/GoogleDrivePanel';
import { GmailPanel } from '../workspace/GmailPanel';
import { SlackPanel } from '../workspace/SlackPanel';
import { ConnectorsPanel } from '../workspace/ConnectorsPanel';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { isGitHubEnabled } from '../../services/githubData';
import { isSlackConnected } from '../../services/slack';
import { useSlackPolling } from '../../hooks/useSlackPolling';
import { BrandProvider, useBrandConfig } from '../../hooks/useBrandConfig';
import { RefreshButton } from '../shared/RefreshButton';
import { BacklogView } from '../workspace/BacklogView';
import { SprintListView } from '../workspace/SprintListView';
import { SprintHealthScore } from '../shared/SprintHealthScore';
import { ConfluencePanel } from '../workspace/ConfluencePanel';
import { UserProfile } from '../../services/userProfiles';
import { CardHeaderWithFullscreen } from '../shared/FullscreenPanel';
import { SprintSkeleton, BacklogSkeleton, KanbanSkeleton, DocsSkeleton } from '../shared/TabSkeleton';
import { SectionErrorBoundary } from '../shared/SectionErrorBoundary';
import { useFeatureHints } from '../../hooks/useFeatureHints';
import { FeatureHint } from '../shared/FeatureHint';
import { HealthCheckPanel } from '../workspace/HealthCheckPanel';
import { useHealthCheck } from '../../hooks/useHealthCheck';
import { GoogleSheetsPanel } from '../workspace/GoogleSheetsPanel';
import { GoogleDocsPanel } from '../workspace/GoogleDocsPanel';
import { GoogleSlidesPanel } from '../workspace/GoogleSlidesPanel';
import { GoogleFormsPanel } from '../workspace/GoogleFormsPanel';
import { TermsPanel } from '../workspace/TermsPanel';
import { PrivacyPolicyPanel } from '../workspace/PrivacyPolicyPanel';
import { NotionPanel } from '../workspace/NotionPanel';
import { GlobalDebugPanel } from '../shared/GlobalDebugPanel';
import { WorkiumLogo } from '../brand/WorkiumLogo';
interface WorkspaceHubProps {
  onLogout?: () => void;
  activeUser?: UserProfile;
}
function WorkspaceHubInner({
  onLogout,
  activeUser
}: WorkspaceHubProps) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'chat' | 'sprint' | 'backlog' | 'kanban' | 'docs' | 'agents' | 'connectors' | 'settings' | 'billing' | 'onboarding' | 'terms' | 'privacy' | 'github' | 'notion' | 'gcalendar' | 'gdrive' | 'gmail' | 'gsheets' | 'gdocs' | 'gslides' | 'gforms' | 'slack' | 'health'>('dashboard');
  const [isFloatingChatOpen, setIsFloatingChatOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardMode, setWizardMode] = useState<'setup' | 'boards'>('setup');
  const [showHistory, setShowHistory] = useState(false);
  const [backlogFilter, setBacklogFilter] = useState<'all' | 'Story' | 'Bug' | 'Task' | 'Epic'>('all');
  const [backlogSort, setBacklogSort] = useState<'priority' | 'points' | 'date'>('priority');
  const [isSwitchingProject, setIsSwitchingProject] = useState(false);
  const {
    isConnected: googleConnected
  } = useGoogleAuth();
  // Feature Hints
  const {
    activeHint,
    dismissHint
  } = useFeatureHints(activeTab);
  // Health Check status for sidebar icon
  const {
    overallStatus: healthStatus
  } = useHealthCheck();
  const slackConnected = isSlackConnected();
  const [githubConnected, setGithubConnected] = useState(() => isGitHubEnabled());
  // Slack polling for notifications
  const {
    newMessageCount: slackNewMessages,
    clearCount: clearSlackCount,
    markChannelSeen
  } = useSlackPolling(slackConnected);
  // Re-check GitHub connection after mount (token may be seeded async by module init)
  useEffect(() => {
    const check = () => setGithubConnected(isGitHubEnabled());
    // Check immediately and after a short delay to catch async seeding
    check();
    const t1 = setTimeout(check, 200);
    const t2 = setTimeout(check, 1000);
    // Also listen for storage changes (cross-tab config updates)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'vsm_github_config') check();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const {
    messages,
    isLoading,
    isStreaming,
    sendMessage,
    sessionId,
    endpointMode,
    setEndpointMode,
    chatMode,
    setChatMode,
    rateFeedback,
    editMessage,
    regenerateResponse,
    setProjectContext
  } = useChat();
  const {
    sessions,
    activeSessionId,
    setActiveSessionId,
    saveSession,
    deleteSession,
    clearAll
  } = useChatHistory();
  const {
    brand,
    updateBrand
  } = useBrandConfig();
  const {
    activeProject,
    isGlobalView,
    setIsGlobalView,
    setRealProjects,
    updateProjectData,
    isRealData,
    dataMode,
    setDataMode
  } = useProjectContext();
  // Apply user-specific brand settings on mount
  useEffect(() => {
    if (activeUser?.brand) {
      updateBrand(activeUser.brand);
    }
  }, [activeUser, updateBrand]);
  // ─── Real Jira Data (only fetch when in live mode) ───────────────────
  const isLive = dataMode === 'live';
  const {
    projects: jiraProjects,
    projectsLoading,
    projectsError,
    sprintSummary,
    sprintLoading,
    allSprints,
    boards,
    boardsLoading,
    backlog: jiraBacklog,
    backlogLoading,
    recentActivity,
    activityLoading,
    kpis: jiraKpis,
    burndownData,
    refreshProjectData
  } = useJiraData(isLive ? activeProject?.key : undefined, isLive ? isGlobalView : undefined, isLive);
  // When Jira projects arrive in live mode, set them as real projects
  useEffect(() => {
    if (!isLive || !Array.isArray(jiraProjects) || jiraProjects.length === 0) return;
    console.log('[WorkspaceHub] Setting real projects:', jiraProjects.map(p => p.key));
    const realProjects = jiraProjects.map((jp, idx) => buildMockProject(jp, null, [], idx));
    setRealProjects(realProjects);
  }, [jiraProjects, isLive, setRealProjects]);
  // When real projects are set and active project changes, fetch its data
  useEffect(() => {
    if (!isLive || !activeProject || isGlobalView) return;
    if (sprintLoading || backlogLoading) return;
    const updates: Partial<typeof activeProject> = {};
    let hasUpdates = false;
    if (sprintSummary && Array.isArray(sprintSummary.issues)) {
      updates.issues = sprintSummary.issues.map(toMockIssue);
      updates.sprint = toMockSprint(sprintSummary.sprint, sprintSummary.issues);
      updates.velocity = sprintSummary.completedPoints;
      updates.completion = sprintSummary.progress;
      updates.status = sprintSummary.progress >= 70 ? 'active' : sprintSummary.progress >= 40 ? 'at-risk' : 'on-hold';
      hasUpdates = true;
    }
    if (Array.isArray(jiraBacklog) && jiraBacklog.length > 0) {
      updates.backlog = jiraBacklog.map(toMockIssue);
      hasUpdates = true;
    }
    if (sprintSummary && updates.sprint && Array.isArray(sprintSummary.issues) && Array.isArray(jiraBacklog)) {
      updates.kpis = buildKPIs(sprintSummary.issues, jiraBacklog, updates.sprint);
      hasUpdates = true;
    }
    if (hasUpdates) {
      updateProjectData(activeProject.key, updates);
    }
  }, [sprintSummary, jiraBacklog, sprintLoading, backlogLoading, activeProject?.key, isGlobalView, isLive]);
  // Sync project context to chat agent
  useEffect(() => {
    if (isGlobalView) {
      setProjectContext({
        key: '',
        name: '',
        isGlobal: true
      });
    } else if (activeProject) {
      setProjectContext({
        key: activeProject.key,
        name: activeProject.name,
        isGlobal: false
      });
    }
    // Trigger fake loading state when switching projects in mock mode
    if (!isLive) {
      setIsSwitchingProject(true);
      const t = setTimeout(() => setIsSwitchingProject(false), 600);
      return () => clearTimeout(t);
    }
  }, [activeProject, isGlobalView, setProjectContext, isLive]);
  const [editingContent, setEditingContent] = useState<string>('');
  const [selectedKanbanBoardId, setSelectedKanbanBoardId] = useState<number | null>(null);
  // Auto-select first Kanban board when boards load
  useEffect(() => {
    if (!isLive || boards.length === 0) return;
    const kanban = boards.filter(b => b.type === 'kanban');
    if (kanban.length > 0 && selectedKanbanBoardId === null) {
      setSelectedKanbanBoardId(kanban[0].id);
    }
  }, [boards, isLive, selectedKanbanBoardId]);
  const handleEdit = (messageId: string) => {
    const content = editMessage(messageId);
    if (content) {
      setEditingContent(content);
      setTimeout(() => setEditingContent(''), 100);
    }
  };
  // Refresh handler for live mode
  const handleRefresh = useCallback(async () => {
    if (isLive && activeProject?.key) {
      await refreshProjectData(activeProject.key);
    }
  }, [isLive, activeProject?.key, refreshProjectData]);
  // Use active project data — with safe defaults to prevent .map() crashes
  const projectIssues = Array.isArray(activeProject?.issues) ? activeProject.issues : [];
  const projectBacklog = Array.isArray(activeProject?.backlog) ? activeProject.backlog : [];
  const projectSprint = activeProject?.sprint || {
    id: '',
    name: 'Sin sprint',
    goal: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: 'Planned' as const,
    progress: 0,
    totalPoints: 0,
    completedPoints: 0
  };
  const projectKpis = Array.isArray(activeProject?.kpis) ? activeProject.kpis : [];
  // Determine if project has Scrum boards (to show/hide Sprint tab)
  // In live mode: check if we have sprints data (only Scrum boards produce sprints)
  // In mock mode: always show Sprint tab
  const hasScrumBoard = isLive ? allSprints.length > 0 || sprintSummary !== null : true;
  // Calculate health score from real sprint data
  const sprintProgress = projectSprint.progress || 0;
  const sprintTotalPts = projectSprint.totalPoints || 0;
  const sprintCompletedPts = projectSprint.completedPoints || 0;
  const blockerCount = projectIssues.filter(i => i.priority === 'High' && (i.status === 'To Do' || i.status === 'In Progress')).length;
  const unestimatedCount = projectIssues.filter(i => !i.points || i.points === 0).length;
  const unassignedCount = projectIssues.filter(i => !i.assignee).length;
  const doneCount = projectIssues.filter(i => i.status === 'Done').length;
  const inProgressCount = projectIssues.filter(i => i.status === 'In Progress').length;
  // Sprint days calculation
  const sprintEnd = new Date(projectSprint.endDate);
  const sprintStart = new Date(projectSprint.startDate);
  const today = new Date();
  const totalDays = Math.max(1, Math.ceil((sprintEnd.getTime() - sprintStart.getTime()) / (1000 * 60 * 60 * 24)));
  const daysElapsed = Math.max(0, Math.ceil((today.getTime() - sprintStart.getTime()) / (1000 * 60 * 60 * 24)));
  const daysRemaining = Math.max(0, Math.ceil((sprintEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  // Health score calculation (0-100)
  const completionScore = sprintTotalPts > 0 ? sprintCompletedPts / sprintTotalPts * 40 : 20;
  const blockerPenalty = Math.min(blockerCount * 10, 30);
  const estimationScore = projectIssues.length > 0 ? (projectIssues.length - unestimatedCount) / projectIssues.length * 15 : 15;
  const assignmentScore = projectIssues.length > 0 ? (projectIssues.length - unassignedCount) / projectIssues.length * 15 : 15;
  const timeScore = totalDays > 0 ? daysElapsed / totalDays <= sprintProgress / 100 + 0.15 ? 15 : 5 : 10;
  const healthScore = Math.min(100, Math.max(0, Math.round(completionScore + estimationScore + assignmentScore + timeScore - blockerPenalty)));
  const chatEndRef = useRef<HTMLDivElement>(null);
  const {
    push: pushNotification
  } = useNotifications();
  // Simulate agent requesting interaction after a delay
  useEffect(() => {
    if (dataMode === 'live') return; // No mock notifications in live mode
    const t = setTimeout(() => {
      pushNotification({
        type: 'agent_interaction',
        title: 'El agente necesita tu confirmación',
        message: '¿Querés que mueva VSM-105 a In Progress y lo asigne a Ana García?',
        source: 'Agent',
        action: {
          label: 'Ir al chat',
          onClick: () => setActiveTab('chat')
        }
      });
    }, 25000); // After 25s
    return () => clearTimeout(t);
  }, [dataMode]);
  // Auto-save session whenever messages change
  useEffect(() => {
    if (messages.filter(m => m.role === 'user').length > 0) {
      saveSession(sessionId, messages);
    }
  }, [messages, sessionId, saveSession]);
  useEffect(() => {
    if (activeTab === 'chat') {
      chatEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages, isLoading, isStreaming, activeTab]);
  const filteredBacklog = projectBacklog.filter(i => backlogFilter === 'all' || i.type === backlogFilter).sort((a, b) => {
    if (backlogSort === 'priority') {
      const order = {
        High: 0,
        Medium: 1,
        Low: 2
      };
      return (order[a.priority] || 2) - (order[b.priority] || 2);
    }
    if (backlogSort === 'points') return (b.points || 0) - (a.points || 0);
    return 0;
  });
  const totalBacklogPoints = projectBacklog.reduce((sum, i) => sum + (i.points || 0), 0);
  const isDashboardLoading = isLive ? sprintLoading || backlogLoading : isSwitchingProject;
  const tabTitles: Record<string, string> = {
    dashboard: 'Dashboard',
    chat: 'Agent Chat',
    sprint: 'Sprint Actual',
    backlog: 'Product Backlog',
    kanban: 'Kanban Board',
    docs: 'Documentación',
    agents: 'Agent Marketplace',
    connectors: 'Conectores',
    settings: 'Configuración',
    billing: 'Facturación y Uso',
    onboarding: 'Onboarding',
    terms: 'Términos y Condiciones',
    privacy: 'Política de Privacidad',
    github: 'GitHub',
    notion: 'Notion',
    gcalendar: 'Google Calendar',
    gdrive: 'Google Drive',
    gmail: 'Gmail',
    gsheets: 'Google Sheets',
    gdocs: 'Google Docs',
    gslides: 'Google Slides',
    gforms: 'Google Forms',
    slack: 'Slack',
    health: 'Health Check'
  };
  if (showWizard) {
    return <SetupWizard mode={wizardMode} onComplete={() => setShowWizard(false)} onCancel={() => setShowWizard(false)} data-id="element-1532" />;
  }
  return <div className="flex h-screen bg-slate-950 overflow-hidden font-sans text-slate-200 relative" data-id="element-1533">
      {/* Navigation Sidebar */}
      <div className="w-64 bg-slate-950 border-r border-slate-800 flex flex-col shrink-0" data-id="element-1534">
        {/* Brand Header */}
        <div className="px-5 py-5 border-b border-slate-800 shrink-0 flex items-center" data-id="element-1535">
          <WorkiumLogo size="md" data-id="element-1536" />
        </div>

        {/* Nav Links */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-1537">
          <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2" data-id="element-1538">
            Global
          </p>

          {/* Home - always goes to global dashboard */}
          <button onClick={() => {
          setIsGlobalView(true);
          setActiveTab('dashboard');
        }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'dashboard' && isGlobalView ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1539">
            <Home size={18} data-id="element-1540" /> Home
          </button>

          {[{
          key: 'docs' as const,
          icon: BookOpen,
          label: 'Docs'
        }, {
          key: 'chat' as const,
          icon: MessageSquare,
          label: 'Agent Chat'
        }, {
          key: 'agents' as const,
          icon: Bot,
          label: 'Agents'
        }, {
          key: 'connectors' as const,
          icon: Plug,
          label: 'Conectores'
        }].map(({
          key,
          icon: Icon,
          label
        }) => <button key={key} onClick={() => setActiveTab(key)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === key || key === 'connectors' && ['github', 'gcalendar', 'gdrive', 'gmail', 'gsheets', 'gdocs', 'gslides', 'gforms', 'slack'].includes(activeTab) ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1541">
              <Icon size={18} data-id="element-1542" /> {label}
              {key === 'agents' && <span className="ml-auto text-xs bg-indigo-900/50 text-indigo-400 px-1.5 py-0.5 rounded-full" data-id="element-1543">
                  New
                </span>}
            </button>)}

          {/* Dynamic connected integrations — visible when connected */}
          {(githubConnected || googleConnected || slackConnected) && <div className="ml-3 pl-3 border-l border-slate-800/60 space-y-0.5 mt-0.5" data-id="element-1544">
              {githubConnected && <button onClick={() => setActiveTab('github')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'github' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1545">
                  <Github size={14} data-id="element-1546" /> GitHub
                </button>}
              <button onClick={() => setActiveTab('notion')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'notion' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1547">
                <FileText size={14} data-id="element-1548" /> Notion
              </button>
              {slackConnected && <button onClick={() => {
            setActiveTab('slack');
            clearSlackCount();
          }} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'slack' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1549">
                  <Hash size={14} data-id="element-1550" /> Slack
                  {slackNewMessages > 0 && activeTab !== 'slack' && <span className="ml-auto text-[10px] bg-purple-600 text-white px-1.5 py-0.5 rounded-full min-w-[18px] text-center font-bold" data-id="element-1551">
                      {slackNewMessages > 99 ? '99+' : slackNewMessages}
                    </span>}
                </button>}
              {googleConnected && <>
                  <button onClick={() => setActiveTab('gcalendar')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gcalendar' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1552">
                    <Calendar size={14} data-id="element-1553" /> Calendar
                  </button>
                  <button onClick={() => setActiveTab('gdrive')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gdrive' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1554">
                    <HardDrive size={14} data-id="element-1555" /> Drive
                  </button>
                  <button onClick={() => setActiveTab('gmail')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gmail' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1556">
                    <Mail size={14} data-id="element-1557" /> Gmail
                  </button>
                  <button onClick={() => setActiveTab('gsheets')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gsheets' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1558">
                    <Table2 size={14} data-id="element-1559" /> Sheets
                  </button>
                  <button onClick={() => setActiveTab('gdocs')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gdocs' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1560">
                    <FileText size={14} data-id="element-1561" /> Docs
                  </button>
                  <button onClick={() => setActiveTab('gslides')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gslides' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1562">
                    <Presentation size={14} data-id="element-1563" /> Slides
                  </button>
                  <button onClick={() => setActiveTab('gforms')} className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'gforms' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-500 hover:bg-slate-900 hover:text-slate-300'}`} data-id="element-1564">
                    <ClipboardList size={14} data-id="element-1565" /> Forms
                  </button>
                </>}
            </div>}

          <div className="h-px bg-slate-800 mx-3 my-2" data-id="element-1566"></div>

          <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4 truncate" title={isGlobalView ? 'Proyecto' : activeProject?.name} data-id="element-1567">
            {isGlobalView ? 'Proyecto' : activeProject?.name}
          </p>

          {isGlobalView && <p className="px-3 text-[10px] text-slate-600 -mt-1 mb-2" data-id="element-1568">
              Seleccioná un proyecto desde Home
            </p>}

          <div className={isGlobalView ? 'opacity-40 pointer-events-none' : ''} data-id="element-1569">
            {/* Project Dashboard */}
            <button onClick={() => setActiveTab('dashboard')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'dashboard' && !isGlobalView ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1570">
              <LayoutGrid size={18} data-id="element-1571" /> Dashboard
            </button>

            {/* Dynamic Boards List */}
            {(() => {
            const scrumBoards = boards.filter(b => b.type === 'scrum' || b.type === 'simple');
            const kanbanBoards = boards.filter(b => b.type === 'kanban');
            return <>
                  {/* SCRUM BOARDS */}
                  {(scrumBoards.length > 0 || !isLive) && <>
                      <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4" data-id="element-1572">
                        Scrum
                      </p>
                      <button onClick={() => setActiveTab('sprint')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'sprint' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1573">
                        <Calendar size={18} data-id="element-1574" /> Sprints
                      </button>
                      {isLive && scrumBoards.map(board => <button key={board.id} onClick={() => setActiveTab('sprint')} className="w-full flex items-center gap-2 px-3 py-1.5 ml-4 rounded text-xs text-slate-500 hover:text-slate-300 hover:bg-slate-900/50 transition-colors truncate" title={board.name} data-id="element-1575">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/40 shrink-0" data-id="element-1576"></div>
                            <span className="truncate" data-id="element-1577">{board.name}</span>
                          </button>)}
                    </>}

                  {/* KANBAN BOARDS */}
                  {(kanbanBoards.length > 0 || !isLive) && <>
                      <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4" data-id="element-1578">
                        Kanban
                      </p>
                      {!isLive ? <button onClick={() => {
                  setSelectedKanbanBoardId(null);
                  setActiveTab('kanban');
                }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'kanban' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1579">
                          <Kanban size={18} data-id="element-1580" /> Kanban Board
                        </button> : kanbanBoards.map(board => <button key={board.id} onClick={() => {
                  setSelectedKanbanBoardId(board.id);
                  setActiveTab('kanban');
                }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'kanban' && selectedKanbanBoardId === board.id ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} title={board.name} data-id="element-1581">
                            <Kanban size={18} className="shrink-0" data-id="element-1582" />
                            <span className="truncate" data-id="element-1583">{board.name}</span>
                          </button>)}
                    </>}

                  {/* BACKLOG */}
                  <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4" data-id="element-1584">
                    Backlog
                  </p>
                  <button onClick={() => setActiveTab('backlog')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'backlog' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1585">
                    <List size={18} data-id="element-1586" /> Backlog
                    <span className="ml-auto text-xs bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded-full" data-id="element-1587">
                      {projectBacklog.length}
                    </span>
                  </button>
                </>;
          })()}
          </div>

          <div className="h-px bg-slate-800 mx-3 my-2" data-id="element-1588"></div>

          <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-4" data-id="element-1589">
            Configuración
          </p>
          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1590">
            <Settings size={18} data-id="element-1591" /> Cuenta
          </button>
          <button onClick={() => setActiveTab('health')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'health' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1592">
            <Activity size={18} className={activeTab === 'health' ? 'text-indigo-400' : healthStatus === 'healthy' ? 'text-emerald-400' : healthStatus === 'degraded' ? 'text-amber-400' : healthStatus === 'critical' ? 'text-red-400' : ''} data-id="element-1593" />{' '}
            Health Check
            {healthStatus === 'critical' && activeTab !== 'health' && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-1594" />}
            {healthStatus === 'degraded' && activeTab !== 'health' && <span className="ml-auto w-2 h-2 rounded-full bg-amber-500" data-id="element-1595" />}
          </button>
          <button onClick={() => setActiveTab('billing')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'billing' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1596">
            <CreditCard size={18} data-id="element-1597" /> Facturación
          </button>
          <button onClick={() => setActiveTab('onboarding')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'onboarding' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1598">
            <Rocket size={18} data-id="element-1599" /> Onboarding
          </button>
          <button onClick={() => setActiveTab('terms')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'terms' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1600">
            <BookOpen size={18} data-id="element-1601" /> Términos
          </button>
          <button onClick={() => setActiveTab('privacy')} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'privacy' ? 'bg-slate-900 text-indigo-400 shadow-sm border border-slate-800' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`} data-id="element-1602">
            <Shield size={18} data-id="element-1603" /> Privacidad
          </button>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-800 shrink-0" data-id="element-1604">
          <div className="flex items-center gap-3" data-id="element-1605">
            <div className="w-8 h-8 rounded-full bg-indigo-900/50 border border-indigo-800 text-indigo-300 font-bold flex items-center justify-center text-xs" data-id="element-1606">
              {activeUser?.initials || 'YO'}
            </div>
            <div className="flex-1 min-w-0" data-id="element-1607">
              <p className="text-sm font-medium text-slate-200 truncate" data-id="element-1608">
                {activeUser?.name || 'Admin User'}
              </p>
              <p className="text-xs text-slate-500 truncate" data-id="element-1609">
                {activeUser?.email || 'admin@acme.com'}
              </p>
            </div>
            {onLogout && <button onClick={onLogout} className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-950/30 transition-colors shrink-0" title="Cerrar sesión" data-id="element-1610">
                <LogOut size={16} data-id="element-1611" />
              </button>}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-slate-950 relative min-w-0 overflow-hidden" data-id="element-1612">
        {/* Header */}
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 lg:px-8 shrink-0 min-w-0 overflow-x-auto" data-id="element-1613">
          <div className="flex items-center gap-2 lg:gap-3 min-w-0 shrink" data-id="element-1614">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center text-sm font-medium text-slate-400 whitespace-nowrap" data-id="element-1615">
              <button onClick={() => {
              setIsGlobalView(true);
              setActiveTab('dashboard');
            }} className="hover:text-indigo-400 transition-colors flex items-center gap-1.5" data-id="element-1616">
                <Home size={14} data-id="element-1617" />
                <span className="hidden sm:inline" data-id="element-1618">Home</span>
              </button>

              {!isGlobalView && activeProject && <>
                  <ChevronRight size={14} className="mx-1 text-slate-600" data-id="element-1619" />
                  <span className="text-slate-300 truncate max-w-[150px] lg:max-w-[200px]" title={activeProject.name} data-id="element-1620">
                    {activeProject.name}
                  </span>
                </>}

              <ChevronRight size={14} className="mx-1 text-slate-600" data-id="element-1621" />
              <h1 className="text-lg lg:text-xl font-bold text-slate-100" data-id="element-1622">
                {tabTitles[activeTab]}
              </h1>
            </div>

            {/* Mock / Live Toggle */}
            <div className="flex items-center bg-slate-800 rounded-full p-0.5 border border-slate-700 shrink-0 ml-4" data-id="element-1623">
              <button onClick={() => setDataMode('mock')} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${dataMode === 'mock' ? 'bg-slate-700 text-slate-200 shadow-sm' : 'text-slate-500 hover:text-slate-400'}`} data-id="element-1624">
                <Terminal size={11} data-id="element-1625" />
                Mock
              </button>
              <button onClick={() => setDataMode('live')} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${dataMode === 'live' ? 'bg-emerald-900/60 text-emerald-400 shadow-sm border border-emerald-800/50' : 'text-slate-500 hover:text-slate-400'}`} data-id="element-1626">
                <Radio size={11} className={dataMode === 'live' ? 'animate-pulse' : ''} data-id="element-1627" />
                Live
              </button>
            </div>
            {dataMode === 'live' && (sprintLoading || backlogLoading || projectsLoading) && <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-950/40 text-indigo-400 text-[10px] font-medium rounded-full border border-indigo-900/40 shrink-0" data-id="element-1628">
                  <Zap size={10} className="animate-pulse" data-id="element-1629" /> Cargando...
                </span>}
            {dataMode === 'live' && projectsError && !projectsLoading && <span className="flex items-center gap-1 px-2 py-0.5 bg-red-950/40 text-red-400 text-[10px] font-medium rounded-full border border-red-900/40 shrink-0 max-w-[200px] truncate" title={projectsError} data-id="element-1630">
                ⚠️ {projectsError.slice(0, 30)}
              </span>}
          </div>
          <div className="flex items-center gap-2 shrink-0" data-id="element-1631">
            {activeTab === 'chat' && <Button variant="outline" size="sm" className={`gap-2 text-xs ${showHistory ? 'text-indigo-400 border-indigo-500/50' : 'text-slate-400'}`} onClick={() => setShowHistory(v => !v)} data-id="element-1632">
                <History size={14} data-id="element-1633" />{' '}
                <span className="hidden xl:inline" data-id="element-1634">Historial</span>
                {sessions.length > 0 && <span className="bg-slate-700 text-slate-400 px-1 rounded text-[10px]" data-id="element-1635">
                    {sessions.length}
                  </span>}
              </Button>}
            <Button variant="outline" size="sm" className="gap-2 text-slate-400" onClick={() => {
            setWizardMode('boards');
            setShowWizard(true);
          }} data-id="element-1636">
              <LayoutGrid size={14} data-id="element-1637" />{' '}
              <span className="hidden xl:inline" data-id="element-1638">Tableros</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-2 text-slate-400" onClick={() => {
            setWizardMode('setup');
            setShowWizard(true);
          }} data-id="element-1639">
              <Sparkles size={14} data-id="element-1640" />{' '}
              <span className="hidden xl:inline" data-id="element-1641">Setup</span>
            </Button>
            <NotificationCenter data-id="element-1642" />
          </div>
        </header>

        {/* Content Switcher */}
        <main className="flex-1 overflow-hidden relative flex min-w-0" data-id="element-1643">
          {/* Feature Hint Banner */}
          <FeatureHint hint={activeHint} onDismiss={dismissHint} data-id="element-1644" />

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (isGlobalView ? <GlobalDashboard data-id="element-1645" /> : <div className="flex-1 overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300" data-id="element-1646">
                <div className="w-full max-w-6xl mx-auto space-y-6 lg:space-y-8" data-id="element-1647">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1648">
                    {projectKpis.map(kpi => <KPICard key={kpi.id} kpi={kpi} isLoading={isDashboardLoading} data-id="element-1649" />)}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1650">
                    <Card className="p-6 bg-slate-900 border-slate-800" data-id="element-1651">
                      <CardHeaderWithFullscreen title="Sprint Progress" headerActions={<RefreshButton onRefresh={handleRefresh} data-id="element-1653" />} data-id="element-1652">
                        {isLive && burndownData.length === 0 && !sprintLoading ? <div className="h-32 flex flex-col items-center justify-center text-center" data-id="element-1654">
                            <p className="text-xs text-slate-600" data-id="element-1655">
                              Sin datos de burndown
                            </p>
                            <p className="text-[10px] text-slate-700 mt-1" data-id="element-1656">
                              El sprint no tiene issues con story points
                            </p>
                          </div> : <SprintBurndown data={isLive ? burndownData : undefined} isLoading={isDashboardLoading} data-id="element-1657" />}
                      </CardHeaderWithFullscreen>
                    </Card>
                    <Card className="p-6 bg-slate-900 border-slate-800" data-id="element-1658">
                      <CardHeaderWithFullscreen title="Sprint Health" headerActions={<RefreshButton onRefresh={handleRefresh} data-id="element-1660" />} data-id="element-1659">
                        <SprintHealthScore score={healthScore} metrics={{
                    completion: sprintProgress,
                    completedPts: sprintCompletedPts,
                    totalPts: sprintTotalPts,
                    blockers: blockerCount,
                    unestimated: unestimatedCount,
                    daysRemaining,
                    totalDays,
                    doneCount,
                    inProgressCount,
                    totalIssues: projectIssues.length
                  }} isLoading={isDashboardLoading} data-id="element-1661" />
                      </CardHeaderWithFullscreen>
                    </Card>
                    <Card className="p-6 bg-slate-900 border-slate-800 flex flex-col" data-id="element-1662">
                      <ActivityFeed isLive={isLive} isLoading={isDashboardLoading} data-id="element-1663" />
                    </Card>
                  </div>
                </div>
              </div>)}

          {/* CHAT TAB */}
          {activeTab === 'chat' && <div className="flex-1 flex overflow-hidden" data-id="element-1664">
              {/* History Sidebar */}
              {showHistory && <ChatHistorySidebar sessions={sessions} activeSessionId={activeSessionId} currentSessionId={sessionId} onSelectSession={session => {
            setActiveSessionId(session.id);
            setShowHistory(false);
          }} onDeleteSession={deleteSession} onClearAll={clearAll} onClose={() => setShowHistory(false)} data-id="element-1665" />}

              {/* Chat Area */}
              <div className="flex-1 flex flex-col animate-in fade-in duration-300 bg-slate-950 min-w-0" data-id="element-1666">
                {/* Sprint Context Bar */}
                <SprintContextBar data-id="element-1667" />

                <div className="flex-1 overflow-y-auto p-8" data-id="element-1668">
                  <div className="max-w-3xl mx-auto" data-id="element-1669">
                    <div className="flex justify-center mb-8" data-id="element-1670">
                      <AgentAvatar size="lg" data-id="element-1671" />
                    </div>
                    <h2 className="text-center text-2xl font-bold text-slate-100 mb-2" data-id="element-1672">
                      ¿En qué puedo ayudarte hoy?
                    </h2>
                    <p className="text-center text-slate-500 mb-12" data-id="element-1673">
                      {brand.tagline}
                    </p>

                    {messages.map((msg, idx) => <ChatMessage key={msg.id} message={msg} onFollowup={sendMessage} isStreaming={isStreaming && idx === messages.length - 1 && msg.role === 'agent'} onFeedback={rateFeedback} onEdit={handleEdit} onRegenerate={regenerateResponse} data-id="element-1674" />)}

                    <div ref={chatEndRef} data-id="element-1675" />
                  </div>
                </div>

                <div className="p-6 bg-slate-900 border-t border-slate-800 shrink-0" data-id="element-1676">
                  <div className="max-w-3xl mx-auto" data-id="element-1677">
                    <ChatInput onSend={sendMessage} isLoading={isLoading || isStreaming} selectedAgent={endpointMode} onAgentChange={setEndpointMode} selectedMode={chatMode} onModeChange={setChatMode} initialValue={editingContent} data-id="element-1678" />
                  </div>
                </div>
              </div>
            </div>}

          {/* SPRINT TAB */}
          {activeTab === 'sprint' && (isDashboardLoading ? <SprintSkeleton data-id="element-1679" /> : <SprintListView projectIssues={projectIssues} projectBacklog={projectBacklog} activeSprint={projectSprint} isLive={isLive} onRefresh={handleRefresh} data-id="element-1680" />)}

          {/* BACKLOG TAB */}
          {activeTab === 'backlog' && (isDashboardLoading ? <BacklogSkeleton data-id="element-1681" /> : <BacklogView backlogIssues={projectBacklog} sprintIssues={projectIssues} jiraBaseUrl={brand.jiraBaseUrl} onRefresh={handleRefresh} data-id="element-1682" />)}

          {/* KANBAN TAB */}
          {activeTab === 'kanban' && (isDashboardLoading ? <KanbanSkeleton data-id="element-1683" /> : <KanbanBoard boardId={selectedKanbanBoardId} boardName={boards.find(b => b.id === selectedKanbanBoardId)?.name} data-id="element-1684" />)}

          {/* DOCS TAB */}
          {activeTab === 'docs' && <SectionErrorBoundary fallbackTitle="Error al cargar Confluence" data-id="element-1685">
              <ConfluencePanel data-id="element-1686" />
            </SectionErrorBoundary>}

          {/* GITHUB TAB */}
          {activeTab === 'github' && <SectionErrorBoundary fallbackTitle="Error al cargar GitHub" data-id="element-1687">
              <GitHubPanel data-id="element-1688" />
            </SectionErrorBoundary>}

          {/* NOTION TAB */}
          {activeTab === 'notion' && <SectionErrorBoundary fallbackTitle="Error al cargar Notion" data-id="element-1689">
              <NotionPanel data-id="element-1690" />
            </SectionErrorBoundary>}

          {/* SLACK TAB */}
          {activeTab === 'slack' && <SectionErrorBoundary fallbackTitle="Error al cargar Slack" data-id="element-1691">
              <SlackPanel data-id="element-1692" />
            </SectionErrorBoundary>}

          {/* GOOGLE WORKSPACE TABS */}
          {activeTab === 'gcalendar' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Calendar" data-id="element-1693">
              <GoogleCalendarPanel data-id="element-1694" />
            </SectionErrorBoundary>}
          {activeTab === 'gdrive' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Drive" data-id="element-1695">
              <GoogleDrivePanel data-id="element-1696" />
            </SectionErrorBoundary>}
          {activeTab === 'gmail' && <SectionErrorBoundary fallbackTitle="Error al cargar Gmail" data-id="element-1697">
              <GmailPanel data-id="element-1698" />
            </SectionErrorBoundary>}

          {/* GOOGLE SHEETS TAB */}
          {activeTab === 'gsheets' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Sheets" data-id="element-1699">
              <GoogleSheetsPanel data-id="element-1700" />
            </SectionErrorBoundary>}

          {/* GOOGLE DOCS TAB */}
          {activeTab === 'gdocs' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Docs" data-id="element-1701">
              <GoogleDocsPanel data-id="element-1702" />
            </SectionErrorBoundary>}

          {/* GOOGLE SLIDES TAB */}
          {activeTab === 'gslides' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Slides" data-id="element-1703">
              <GoogleSlidesPanel data-id="element-1704" />
            </SectionErrorBoundary>}

          {/* GOOGLE FORMS TAB */}
          {activeTab === 'gforms' && <SectionErrorBoundary fallbackTitle="Error al cargar Google Forms" data-id="element-1705">
              <GoogleFormsPanel data-id="element-1706" />
            </SectionErrorBoundary>}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && <SettingsPanel activeUser={activeUser} onLogout={onLogout} data-id="element-1707" />}

          {/* BILLING TAB */}
          {activeTab === 'billing' && <BillingPanel data-id="element-1708" />}

          {/* ONBOARDING TAB */}
          {activeTab === 'onboarding' && <OnboardingPanel onComplete={() => setActiveTab('dashboard')} data-id="element-1709" />}

          {/* TERMS TAB */}
          {activeTab === 'terms' && <TermsPanel data-id="element-1710" />}

          {/* PRIVACY TAB */}
          {activeTab === 'privacy' && <PrivacyPolicyPanel data-id="element-1711" />}

          {/* AGENTS MARKETPLACE TAB */}
          {activeTab === 'agents' && <AgentMarketplace data-id="element-1712" />}

          {/* CONNECTORS TAB */}
          {activeTab === 'connectors' && <ConnectorsPanel onNavigate={setActiveTab} data-id="element-1713" />}

          {/* HEALTH CHECK TAB */}
          {activeTab === 'health' && <HealthCheckPanel onNavigate={setActiveTab} data-id="element-1714" />}
        </main>
      </div>

      {/* Floating Chat Widget */}
      {activeTab !== 'chat' && <FloatingChat messages={messages} isLoading={isLoading} onSend={sendMessage} onExpand={() => {
      setIsFloatingChatOpen(false);
      setActiveTab('chat');
    }} isOpen={isFloatingChatOpen} setIsOpen={setIsFloatingChatOpen} data-id="element-1715" />}

      {/* Global Debug Panel (Hidden by default, triggered by Ctrl+Shift+D) */}
      <GlobalDebugPanel data-id="element-1716" />
    </div>;
}
export function WorkspaceHub({
  onLogout,
  activeUser
}: WorkspaceHubProps) {
  return <BrandProvider data-id="element-1717">
      <ProjectProvider data-id="element-1718">
        <WorkspaceHubWrapper onLogout={onLogout} activeUser={activeUser} data-id="element-1719" />
      </ProjectProvider>
    </BrandProvider>;
}
function WorkspaceHubWrapper({
  onLogout,
  activeUser
}: WorkspaceHubProps) {
  const {
    dataMode
  } = useProjectContext();
  return <NotificationsProvider dataMode={dataMode} data-id="element-1720">
      <WorkspaceHubInner onLogout={onLogout} activeUser={activeUser} data-id="element-1721" />
      <ToastContainer data-id="element-1722" />
    </NotificationsProvider>;
}